import {
    CartItemAddInput,
    CartItemUpdateInput,
    DeliveryTypeCategory,
    OpenTabCartFragment,
} from '@/features/graphql/generated/graphql'
import useAccountRecords from '@/features/openTabs/contexts/OpenTabsData/hooks/useAccountRecords'
import { logger } from '@/features/openTabs/logger'
import {
    OpenTabCartItemAddDocument,
    OpenTabCartItemRemoveDocument,
    OpenTabCartItemUpdateDocument,
    OpenTabSubmissionCreateDocument,
} from '@/features/openTabs/mutations'
import { OpenTabCatalogDocument, OpenTabDraftCartDocument } from '@/features/openTabs/queries'
import { OpenAccountCatalogValue, OpenTabAccount } from '@/features/openTabs/types'
import { toCartPriceBreakdown, toCatalog } from '@/features/openTabs/utils/backendTransforms'
import { toDraftCartItemUpdate } from '@/features/openTabs/utils/draftCart'
import { AutomaticContainerSettingsQuery } from '@/features/pos/components/OrderFinalization/queries'
import { useHandleErrorWithToast } from '@/hooks/useHandleErrorWithToast'
import { useApolloClient, useMutation, useQuery } from '@apollo/client'
import currencyJs from 'currency.js'
import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react'

interface UseCatalogDraftArgs {
    selectedAccount: OpenTabAccount | undefined
    refetchSummaries: ReturnType<typeof useAccountRecords>['refetchSummaries']
    refetchDetail: ReturnType<typeof useAccountRecords>['refetchDetail']
    accountDataStale: boolean
    settleAccountInFlightRef: RefObject<boolean>
}

const useCatalogDraft = ({
    selectedAccount,
    refetchSummaries,
    refetchDetail,
    accountDataStale,
    settleAccountInFlightRef,
}: UseCatalogDraftArgs) => {
    const handleError = useHandleErrorWithToast(logger)
    const client = useApolloClient()

    const [catalogCart, setCatalogCart] = useState<OpenTabCartFragment>()
    const [editingCatalogItemId, setEditingCatalogItemId] = useState<string | null>(null)
    const catalogMutationInFlightRef = useRef(false)
    const requestInFlightRef = useRef(false)
    const recoveryRef = useRef<{
        cartKey: string
        draftVersion: number
        refreshAccount: boolean
    } | null>(null)
    const [catalogCartRefreshFailed, setCatalogCartRefreshFailed] = useState(false)
    const catalogDraftVersionRef = useRef(0)
    const clearCatalogCart = useCallback(() => {
        catalogDraftVersionRef.current += 1
        recoveryRef.current = null
        setCatalogCartRefreshFailed(false)
        catalogMutationInFlightRef.current = requestInFlightRef.current
        setCatalogCart(undefined)
        setEditingCatalogItemId(null)
    }, [])

    const {
        data: catalogData,
        error: catalogError,
        loading: catalogQueryLoading,
        refetch: refetchCatalog,
    } = useQuery(OpenTabCatalogDocument, { fetchPolicy: 'cache-and-network' })
    const {
        data: containerSettingsData,
        error: containerSettingsError,
        loading: containerSettingsLoading,
        refetch: refetchContainerSettings,
    } = useQuery(AutomaticContainerSettingsQuery, { fetchPolicy: 'cache-and-network' })
    const catalogPricingReady = !!containerSettingsData
    const attachAutomaticContainers =
        containerSettingsData?.automaticContainerSettings.find(
            ({ deliveryTypeCategory }) => deliveryTypeCategory === DeliveryTypeCategory.EatIn
        )?.attachAutomatically ?? false
    const catalogCartPriceBreakdown = useMemo(
        () => toCartPriceBreakdown(catalogCart, attachAutomaticContainers),
        [attachAutomaticContainers, catalogCart]
    )

    useEffect(() => {
        if (!catalogQueryLoading && catalogError) void handleError(catalogError)
    }, [catalogError, catalogQueryLoading, handleError])
    useEffect(() => {
        if (!containerSettingsLoading && containerSettingsError)
            void handleError(containerSettingsError)
    }, [containerSettingsError, containerSettingsLoading, handleError])

    const catalog = useMemo(
        () => toCatalog(catalogData?.categories ?? []),
        [catalogData?.categories]
    )

    const [addCartItem, { loading: addingCatalogItem }] = useMutation(OpenTabCartItemAddDocument)
    const [removeCartItem, { loading: removingCatalogItem }] = useMutation(
        OpenTabCartItemRemoveDocument
    )
    const [updateCartItem, { loading: updatingCatalogItem }] = useMutation(
        OpenTabCartItemUpdateDocument
    )
    const [createSubmission, { loading: submittingCatalogCart }] = useMutation(
        OpenTabSubmissionCreateDocument
    )

    const reconcileCatalogCart = useCallback(async (): Promise<boolean> => {
        const recovery = recoveryRef.current
        if (!recovery) return true

        try {
            const [{ data }] = await Promise.all([
                client.query({
                    query: OpenTabDraftCartDocument,
                    variables: { cartKey: recovery.cartKey },
                    fetchPolicy: 'network-only',
                    context: { queryDeduplication: false },
                }),
                recovery.refreshAccount
                    ? Promise.all([refetchSummaries(), refetchDetail()])
                    : Promise.resolve(),
            ])
            if (recovery.draftVersion !== catalogDraftVersionRef.current) return false
            const cart = data.cart
            if (!cart || (cart.key && cart.key !== recovery.cartKey))
                throw new Error('Draft cart refresh returned no matching cart.')

            // A successfully appended cart is consumed by the backend and has no key.
            // Never infer append success by comparing products with another submission.
            setCatalogCart(cart.key && cart.totalQuantity > 0 ? cart : undefined)
            setEditingCatalogItemId((current) =>
                cart.items.some((item) => item.uuid === current) ? current : null
            )
            recoveryRef.current = null
            setCatalogCartRefreshFailed(false)
            return true
        } catch (error) {
            if (recovery.draftVersion === catalogDraftVersionRef.current) {
                setCatalogCartRefreshFailed(true)
                void handleError(error)
            }
            return false
        }
    }, [client, handleError, refetchDetail, refetchSummaries])

    const recoverMutation = useCallback(
        async (error: unknown, draftVersion: number, cartKey?: string, refreshAccount = false) => {
            void handleError(error)
            if (draftVersion !== catalogDraftVersionRef.current || !cartKey) return

            recoveryRef.current = { cartKey, draftVersion, refreshAccount }
            await reconcileCatalogCart()
        },
        [handleError, reconcileCatalogCart]
    )
    const retryCatalogCart = useCallback(async (): Promise<boolean> => {
        if (requestInFlightRef.current || !recoveryRef.current) return false

        requestInFlightRef.current = true
        catalogMutationInFlightRef.current = true
        try {
            return await reconcileCatalogCart()
        } finally {
            requestInFlightRef.current = false
            catalogMutationInFlightRef.current = !!recoveryRef.current
        }
    }, [reconcileCatalogCart])
    const addCatalogItem = useCallback(
        async (input: CartItemAddInput): Promise<boolean> => {
            if (
                !catalogPricingReady ||
                accountDataStale ||
                selectedAccount?.status !== 'open' ||
                addingCatalogItem ||
                removingCatalogItem ||
                submittingCatalogCart ||
                catalogMutationInFlightRef.current ||
                settleAccountInFlightRef.current
            ) {
                return false
            }

            requestInFlightRef.current = true
            catalogMutationInFlightRef.current = true
            const draftVersion = catalogDraftVersionRef.current
            try {
                const result = await addCartItem({
                    variables: { cartKey: catalogCart?.key, itemAdd: input },
                })
                const nextCart = result.data?.cartItemAdd
                if (draftVersion !== catalogDraftVersionRef.current) return false
                if (!nextCart?.key) throw new Error('Draft cart update returned no cart.')

                setCatalogCart(nextCart)

                return true
            } catch (error) {
                await recoverMutation(error, draftVersion, catalogCart?.key ?? undefined)
                return false
            } finally {
                requestInFlightRef.current = false
                catalogMutationInFlightRef.current = !!recoveryRef.current
            }
        },
        [
            addCartItem,
            recoverMutation,
            addingCatalogItem,
            catalogCart?.key,
            catalogPricingReady,
            accountDataStale,
            removingCatalogItem,
            selectedAccount?.status,
            settleAccountInFlightRef,
            submittingCatalogCart,
        ]
    )

    const removeCatalogItem = useCallback(
        async (itemId: string): Promise<boolean> => {
            if (
                accountDataStale ||
                !catalogCart?.key ||
                selectedAccount?.status !== 'open' ||
                addingCatalogItem ||
                removingCatalogItem ||
                submittingCatalogCart ||
                catalogMutationInFlightRef.current ||
                settleAccountInFlightRef.current
            ) {
                return false
            }

            const item = catalogCart.items.find((candidate) => candidate.uuid === itemId)
            if (!item) return false

            requestInFlightRef.current = true
            catalogMutationInFlightRef.current = true
            const draftVersion = catalogDraftVersionRef.current
            try {
                const result = await removeCartItem({
                    variables: { cartKey: catalogCart.key, itemUuid: item.uuid },
                })
                const nextCart = result.data?.cartItemRemove
                if (draftVersion !== catalogDraftVersionRef.current) return false
                if (!nextCart) throw new Error('Draft cart removal returned no cart.')

                setCatalogCart(nextCart.totalQuantity > 0 ? nextCart : undefined)
                setEditingCatalogItemId((current) => (current === itemId ? null : current))

                return true
            } catch (error) {
                await recoverMutation(error, draftVersion, catalogCart?.key ?? undefined)
                return false
            } finally {
                requestInFlightRef.current = false
                catalogMutationInFlightRef.current = !!recoveryRef.current
            }
        },
        [
            addingCatalogItem,
            catalogCart,
            accountDataStale,
            removeCartItem,
            recoverMutation,
            removingCatalogItem,
            selectedAccount?.status,
            settleAccountInFlightRef,
            submittingCatalogCart,
        ]
    )

    const removeCatalogProduct = useCallback(
        async (productId: string): Promise<boolean> => {
            const item = catalogCart?.items.find(
                (candidate) => candidate.productVariant.product.uuid === productId
            )

            return item ? removeCatalogItem(item.uuid) : false
        },
        [catalogCart, removeCatalogItem]
    )

    const updateCatalogItem = useCallback(
        async (input: CartItemUpdateInput): Promise<boolean> => {
            if (
                accountDataStale ||
                !catalogCart?.key ||
                selectedAccount?.status !== 'open' ||
                addingCatalogItem ||
                removingCatalogItem ||
                updatingCatalogItem ||
                submittingCatalogCart ||
                catalogMutationInFlightRef.current ||
                settleAccountInFlightRef.current
            ) {
                return false
            }

            const item = catalogCart.items.find(
                (candidate) => candidate.uuid === input.cartItemUuid
            )
            if (!item) return false

            const quantity = input.quantity ?? item.quantity
            if (!Number.isInteger(quantity) || quantity < 1) return false

            requestInFlightRef.current = true
            catalogMutationInFlightRef.current = true
            const draftVersion = catalogDraftVersionRef.current
            try {
                const result = await updateCartItem({
                    variables: {
                        cartKey: catalogCart.key,
                        itemUpdate: { ...input, quantity },
                    },
                })
                const nextCart = result.data?.cartItemUpdate
                if (draftVersion !== catalogDraftVersionRef.current) return false
                if (!nextCart?.key) throw new Error('Draft cart update returned no cart.')

                setCatalogCart(nextCart)
                return true
            } catch (error) {
                await recoverMutation(error, draftVersion, catalogCart?.key ?? undefined)
                return false
            } finally {
                requestInFlightRef.current = false
                catalogMutationInFlightRef.current = !!recoveryRef.current
            }
        },
        [
            addingCatalogItem,
            catalogCart,
            accountDataStale,
            removingCatalogItem,
            selectedAccount?.status,
            settleAccountInFlightRef,
            submittingCatalogCart,
            updateCartItem,
            recoverMutation,
            updatingCatalogItem,
        ]
    )

    const changeCatalogItemQuantity = useCallback(
        async (itemId: string, change: number): Promise<boolean> => {
            const item = catalogCart?.items.find((candidate) => candidate.uuid === itemId)
            if (!item || !Number.isInteger(change) || change === 0) return false

            return updateCatalogItem({
                ...toDraftCartItemUpdate(item),
                quantity: item.quantity + change,
            })
        },
        [catalogCart, updateCatalogItem]
    )

    const editCatalogItem = useCallback(
        (itemId: string) => {
            if (
                accountDataStale ||
                selectedAccount?.status !== 'open' ||
                catalogMutationInFlightRef.current ||
                settleAccountInFlightRef.current ||
                !catalogCart?.items.some((item) => item.uuid === itemId)
            ) {
                return
            }

            setEditingCatalogItemId(itemId)
        },
        [accountDataStale, catalogCart?.items, selectedAccount?.status, settleAccountInFlightRef]
    )
    const stopEditingCatalogItem = useCallback(() => setEditingCatalogItemId(null), [])

    const submitCatalogCart = useCallback(
        async (accountId: string): Promise<boolean> => {
            if (
                !catalogPricingReady ||
                accountDataStale ||
                !catalogCart?.key ||
                catalogCart.totalQuantity <= 0 ||
                selectedAccount?.id !== accountId ||
                selectedAccount.status !== 'open' ||
                addingCatalogItem ||
                removingCatalogItem ||
                submittingCatalogCart ||
                catalogMutationInFlightRef.current ||
                settleAccountInFlightRef.current
            ) {
                return false
            }

            requestInFlightRef.current = true
            catalogMutationInFlightRef.current = true
            const draftVersion = catalogDraftVersionRef.current
            try {
                const result = await createSubmission({
                    variables: { openTabUuid: accountId, cartKey: catalogCart.key },
                })
                if (!result.data?.openTabSubmissionCreate.uuid)
                    throw new Error('Open tab submission returned no submission.')

                void Promise.allSettled([refetchSummaries(), refetchDetail()])

                if (draftVersion !== catalogDraftVersionRef.current) return false

                clearCatalogCart()

                return true
            } catch (error) {
                await recoverMutation(error, draftVersion, catalogCart.key, true)
                return false
            } finally {
                requestInFlightRef.current = false
                catalogMutationInFlightRef.current = !!recoveryRef.current
            }
        },
        [
            addingCatalogItem,
            catalogCart,
            accountDataStale,
            catalogPricingReady,
            clearCatalogCart,
            refetchDetail,
            createSubmission,
            recoverMutation,
            refetchSummaries,
            removingCatalogItem,
            selectedAccount,
            settleAccountInFlightRef,
            submittingCatalogCart,
        ]
    )

    const value = useMemo<OpenAccountCatalogValue>(
        () => ({
            catalogCartRefreshFailed,
            retryCatalogCart,
            catalogLoading:
                (catalogQueryLoading && !catalogData) ||
                (containerSettingsLoading && !catalogPricingReady),
            catalogFailed:
                (!!catalogError && !catalogData) ||
                (!!containerSettingsError && !catalogPricingReady),
            addingCatalogItem:
                catalogCartRefreshFailed ||
                !catalogPricingReady ||
                accountDataStale ||
                addingCatalogItem ||
                removingCatalogItem ||
                updatingCatalogItem,
            submittingCatalogCart,
            catalogCategories: catalog.categories,
            catalogSections: catalog.sections,
            catalogCartProductIds:
                catalogCart?.items.map((item) => item.productVariant.product.uuid) ?? [],
            catalogCartItems: catalogCart?.items ?? [],
            editingCatalogItemId,
            catalogCartTotalAmount: catalogCart
                ? currencyJs(catalogCartPriceBreakdown.productsAmount)
                      .add(catalogCartPriceBreakdown.containerAmount)
                      .value.toFixed(2)
                : '0',
            catalogCartPriceBreakdown,
            catalogCartTotalQuantity: catalogCart?.totalQuantity ?? 0,
            addCatalogItem,
            updateCatalogItem,
            removeCatalogItem,
            changeCatalogItemQuantity,
            editCatalogItem,
            stopEditingCatalogItem,
            removeCatalogProduct,
            clearCatalogCart,
            submitCatalogCart,
        }),
        [
            catalogCartRefreshFailed,
            retryCatalogCart,
            catalogQueryLoading,
            catalogData,
            containerSettingsLoading,
            catalogPricingReady,
            accountDataStale,
            catalogError,
            containerSettingsError,
            addingCatalogItem,
            removingCatalogItem,
            updatingCatalogItem,
            submittingCatalogCart,
            catalog.categories,
            catalog.sections,
            catalogCart,
            editingCatalogItemId,
            catalogCartPriceBreakdown,
            addCatalogItem,
            updateCatalogItem,
            removeCatalogItem,
            changeCatalogItemQuantity,
            editCatalogItem,
            stopEditingCatalogItem,
            removeCatalogProduct,
            clearCatalogCart,
            submitCatalogCart,
        ]
    )

    return {
        value,
        catalogMutationInFlightRef,
        catalogDraftVersionRef,
        refetchCatalog,
        refetchContainerSettings,
    }
}

export default useCatalogDraft
